import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { Shield, Users, Database, Settings, Activity, AlertCircle } from "lucide-react";
import { Link, Navigate } from "react-router-dom";

export default function Admin() {
  const { profile } = useAuth();

  // Only admins can access this page
  if (profile?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const adminStats = [
    { label: "Total Users", value: "0", icon: Users, color: "text-blue-600" },
    { label: "Active Businesses", value: "0", icon: Database, color: "text-green-600" },
    { label: "System Status", value: "Operational", icon: Activity, color: "text-emerald-600" },
    { label: "Pending Issues", value: "0", icon: AlertCircle, color: "text-amber-600" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-figgy-600" />
              <h1 className="text-2xl font-semibold">Admin Panel</h1>
            </div>
            <p className="text-muted-foreground">System administration and management</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {adminStats.map((stat) => (
              <Card key={stat.label} className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="text-2xl font-semibold mt-1">{stat.value}</p>
                  </div>
                  <stat.icon className={`h-8 w-8 ${stat.color}`} />
                </div>
              </Card>
            ))}
          </div>

          {/* Admin Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* User Management */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-5 w-5 text-figgy-600" />
                <h2 className="text-lg font-semibold">User Management</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                View and manage all users in the system
              </p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Users</span>
                  <span className="font-medium">0</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Active Today</span>
                  <span className="font-medium">0</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">New This Week</span>
                  <span className="font-medium">0</span>
                </div>
              </div>
            </Card>

            {/* Business Management */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Database className="h-5 w-5 text-figgy-600" />
                <h2 className="text-lg font-semibold">Business Management</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Manage business profiles and settings
              </p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Businesses</span>
                  <span className="font-medium">0</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Active Businesses</span>
                  <span className="font-medium">0</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Pending Approval</span>
                  <span className="font-medium">0</span>
                </div>
              </div>
            </Card>

            {/* Draft Builder */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="h-5 w-5 text-figgy-600" />
                <h2 className="text-lg font-semibold">Draft Builder</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Create and manage premium Figgy preview pages for outreach leads.
              </p>
              <Link
                to="/admin/drafts"
                className="text-sm font-medium text-figgy-600 hover:underline"
              >
                Open Draft Builder →
              </Link>
            </Card>

            {/* System Configuration */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="h-5 w-5 text-figgy-600" />
                <h2 className="text-lg font-semibold">System Configuration</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Configure system-wide settings and features
              </p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Feature Flags</span>
                  <span className="font-medium">Manage</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Email Templates</span>
                  <span className="font-medium">Configure</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">API Keys</span>
                  <span className="font-medium">View</span>
                </div>
              </div>
            </Card>

            {/* System Health */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="h-5 w-5 text-figgy-600" />
                <h2 className="text-lg font-semibold">System Health</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Monitor system performance and health
              </p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">API Status</span>
                  <span className="font-medium text-emerald-600">Operational</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Database</span>
                  <span className="font-medium text-emerald-600">Healthy</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Response Time</span>
                  <span className="font-medium">~50ms</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
